function hasCampaignAccessCookie(cookieName = 'farlo_campaign_access') {
	return document.cookie
		.split(';')
		.map((c) => c.trim())
		.some((c) => c.startsWith(cookieName + '='));
}

function applyCampaignPricePoints(root) {
	if (!root) {
		return;
	}

	// Only actually change text if the cookie exists
	if (!hasCampaignAccessCookie()) {
		return;
	}

	const labels = root.querySelectorAll('[data-price-label]');
	labels.forEach((labelEl) => {
		const span = labelEl.querySelector('span');
		if (!span) {
			return;
		}

		const ds = span.dataset;
		let pricePoints = '';

		for (const key in ds) {
			if (Object.prototype.hasOwnProperty.call(ds, key) && key.endsWith('PricePoints')) {
				pricePoints = ds[ key ];
				break;
			}
		}

		if (pricePoints) {
			span.textContent = pricePoints;
		} else {
			span.textContent = 'Click For Info';
		}
	});
}

export async function hydrateCampaignShows() {
	const hook = document.querySelector('[data-campaign-hydrate]');
	if (!hook) {
		return;
	}

	const endpoint = hook.dataset.endpoint;
	const brand = hook.dataset.brand;
	const phase = hook.dataset.phase;
	const pageSlug = hook.dataset.pageSlug;
	const initialOffset = parseInt(hook.dataset.offset || '0', 10);
	const chunk = parseInt(hook.dataset.chunk || '12', 10);
	const total = parseInt(hook.dataset.total || '0', 10);
	const mode = hook.dataset.mode || 'scroll';
	const priorityIso = hook.dataset.priorityIso || '';
	const revealIso = hook.dataset.revealIso || '';

	// Get filter params from data attribute OR from URL
	let filterParams = {};
	if (hook.dataset.filterParams) {
		try {
			filterParams = JSON.parse(hook.dataset.filterParams);
		} catch (e) {
			// Ignore parse errors
		}
	} else {
		// Fall back to reading from URL for SSR hydration
		const url = new URL(window.location.href);
		['q', 'from', 'to', 'time', 'min', 'max', 'genre', 'sort'].forEach(key => {
			const val = url.searchParams.get(key);
			if (val) {
				filterParams[key] = val;
			}
		});
	}

	const results = document.querySelector('[data-results]');
	const sentinel = document.querySelector('[data-sentinel]');
	if (!results || !endpoint) {
		return;
	}

	let loading = false;
	let done = false;
	let nextOffset = initialOffset;

	const setBusy = (flag) => {
		results.setAttribute('aria-busy', flag ? 'true' : 'false');
	};

	const buildUrl = (offset) => {
		const params = new URLSearchParams({
			brand,
			phase,
			page_slug: pageSlug,
			offset: String(offset),
			limit: String(chunk)
		});

		if (priorityIso) {
			params.set('priority_iso', priorityIso);
		}
		if (revealIso) {
			params.set('reveal_iso', revealIso);
		}

		// Add filter params to API request
		Object.entries(filterParams).forEach(([key, val]) => {
			if (val !== '' && val != null) {
				params.set(key, String(val));
			}
		});

		return `${ endpoint }?${ params.toString() }`;
	};

	async function load() {
		if (loading || done) {
			return;
		}

		loading = true;
		setBusy(true);

		try {
			const res = await fetch(buildUrl(nextOffset), { credentials: 'same-origin' });
			if (!res.ok) {
				console.error('Campaign fetch failed:', res.status);
				return;
			}

			const payload = await res.json();
			const html = (payload && typeof payload.html === 'string') ? payload.html : '';

			if (html) {
				results.insertAdjacentHTML('beforeend', html);
				// Apply price points to newly inserted cards
				applyCampaignPricePoints(results);
			}

			const got = Number.isFinite(payload?.limit) ? Number(payload.limit) : chunk;
			const off = Number.isFinite(payload?.offset) ? Number(payload.offset) : nextOffset;
			nextOffset = off + got;

			if (nextOffset >= total || !html.trim()) {
				done = true;
				sentinel?.remove();
				hook.remove();
			}
		} catch (err) {
			console.error('Campaign hydration error:', err);
		} finally {
			loading = false;
			setBusy(false);
		}
	}

	// Run once on any initial server-rendered cards
	applyCampaignPricePoints(results);

	// Mode: one - load one chunk immediately
	if (mode === 'one') {
		(window.requestIdleCallback || setTimeout)(() => load(), 1);
		return;
	}

	// Mode: all - load all chunks progressively
	if (mode === 'all') {
		const scheduleIdle = (cb) =>
			('requestIdleCallback' in window)
				? window.requestIdleCallback(cb, { timeout: 250 })
				: setTimeout(cb, 1);

		const loop = () => {
			if (!done) {
				load().then(() => !done && scheduleIdle(loop));
			}
		};

		scheduleIdle(loop);
		return;
	}

	if (!sentinel || typeof IntersectionObserver === 'undefined') {
		return;
	}

	const startObserver = () => {
		if (!sentinel || done) {
			return;
		}

		const io = new IntersectionObserver((entries) => {
			for (const e of entries) {
				if (!e.isIntersecting) {
					continue;
				}

				io.unobserve(sentinel);
				load().finally(() => {
					if (!done) {
						io.observe(sentinel);
					}
				});
			}
		}, {
			rootMargin: '200px 0px',
			threshold: 0
		});

		io.observe(sentinel);
	};

	const hasScrolled = () =>
		(window.scrollY || document.documentElement.scrollTop || 0) > 0;

	if (hasScrolled()) {
		startObserver();
	} else {
		const onFirstScroll = () => {
			window.removeEventListener('scroll', onFirstScroll);
			startObserver();
		};
		window.addEventListener('scroll', onFirstScroll, { once: true, passive: true });
	}
}

// Export for window access so campaignFilters can re-init
if (typeof window !== 'undefined') {
	window.hydrateCampaignShows = hydrateCampaignShows;
}